import React, { useMemo } from "react";
import {
  HiOutlineChatAlt2,
  HiOutlineExclamationCircle,
  HiOutlineStar,
  HiOutlineTrash,
} from "react-icons/hi";
import ModalSkeleton from "components/Modals/ModalSkeleton";
import CustomButton from "components/common/Button/CustomButton";
import { formatChatTimestamp } from "../utils/vertex360CopilotChatUtils";

const BULK_LIST_SCROLL_THRESHOLD = 5;

const buildConversationDetails = (conversation) => {
  if (!conversation) return null;

  const messageCount = conversation.messages?.length ?? 0;

  return {
    title: conversation.title || "Untitled chat",
    lastActivity: formatChatTimestamp(conversation.updatedAt),
    messageCount: `${messageCount} message${messageCount === 1 ? "" : "s"}`,
    starred: conversation.starred ? "Yes" : "No",
  };
};

const SUMMARY_ROWS = [
  { key: "title", label: "Chat title" },
  { key: "lastActivity", label: "Last activity" },
  { key: "messageCount", label: "Messages" },
  { key: "starred", label: "Starred" },
];

const BulkDeleteList = ({ conversations = [] }) => {
  const enableScroll = conversations.length >= BULK_LIST_SCROLL_THRESHOLD;

  return (
    <div className="overflow-hidden rounded-xl border border-[#E5E7EB] bg-[#F8FAFC]">
      <div className="border-b border-[#E5E7EB] bg-[#EEF2FB]/70 px-4 py-2.5">
        <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#4A6BB6]">
          Chats to delete ({conversations.length})
        </p>
      </div>
      <ul
        className={
          enableScroll
            ? "custom-scroll max-h-[min(18rem,42vh)] divide-y divide-[#E5E7EB] overflow-y-auto"
            : "divide-y divide-[#E5E7EB]"
        }
      >
        {conversations.map((conversation) => (
          <li
            key={conversation.id}
            className="flex items-start gap-3 px-4 py-3 transition-colors hover:bg-white/80"
          >
            <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#ECF0FD] text-[#4B6CB7] ring-1 ring-inset ring-[#C7D7F5]/80">
              <HiOutlineChatAlt2 className="h-4 w-4" aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-[#0F172A]">
                {conversation.title || "Untitled chat"}
              </p>
              <p className="mt-0.5 text-xs text-[#64748B]">
                {formatChatTimestamp(conversation.updatedAt)}
              </p>
            </div>
            {conversation.starred ? (
              <HiOutlineStar
                className="mt-1 h-4 w-4 shrink-0 text-amber-500"
                aria-label="Starred"
              />
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
};

const Vertex360CopilotDeleteConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  confirming = false,
  errorMessage = null,
  variant = "single",
  conversations = [],
}) => {
  const isBulk = variant === "bulk";
  const count = conversations.length;
  const singleDetails = useMemo(
    () => buildConversationDetails(conversations[0]),
    [conversations]
  );

  const handleClose = () => {
    if (!confirming) onClose?.();
  };

  const title = isBulk
    ? count === 1
      ? "Delete chat?"
      : `Delete ${count} chats?`
    : "Delete chat?";

  const introText = useMemo(() => {
    if (isBulk) {
      if (count === 0) return "No chats selected for deletion.";
      if (count === 1) {
        return "You are about to permanently delete this conversation:";
      }
      return `You are about to permanently delete ${count} conversations:`;
    }

    return singleDetails
      ? "You are about to permanently delete this conversation:"
      : "You are about to permanently delete this chat.";
  }, [isBulk, count, singleDetails]);

  const confirmLabel = useMemo(() => {
    if (confirming) {
      if (isBulk) {
        return `Deleting ${count} chat${count === 1 ? "" : "s"}…`;
      }
      return "Deleting…";
    }

    if (isBulk) {
      return `Delete ${count} chat${count === 1 ? "" : "s"}`;
    }
    return "Yes, delete";
  }, [confirming, isBulk, count]);

  const confirmDisabled = confirming || count === 0;

  const modalFooter = (
    <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm font-medium text-[#0F172A]">Do you want to continue?</p>
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <CustomButton
          type="button"
          variant="secondary"
          label="No, go back"
          clickHandler={handleClose}
          isDisabled={confirming}
          cssClass="!min-w-[7.5rem] justify-center"
        />
        <CustomButton
          type="button"
          variant="danger"
          label={confirmLabel}
          Icon={!confirming ? <HiOutlineTrash className="h-4 w-4" /> : null}
          clickHandler={onConfirm}
          isDisabled={confirmDisabled}
          showLoading={confirming}
          loaderCssClass="!text-red-800 !mr-0"
          cssClass="!min-w-[7.5rem] justify-center"
        />
      </div>
    </div>
  );

  return (
    <ModalSkeleton
      isOpen={isOpen}
      title={title}
      closeModal={confirming ? null : handleClose}
      cssClass={isBulk ? "w-full max-w-xl sm:my-8" : "w-full max-w-lg sm:my-8"}
      bodyPadding="!p-4 sm:!p-5"
      modalFooter={modalFooter}
    >
      <div className="space-y-4">
        <p className="text-sm leading-relaxed text-[#64748B]">{introText}</p>

        {errorMessage ? (
          <p
            className="rounded-lg border border-[#FECACA] bg-[#FEF2F2] px-3 py-2 text-sm font-medium text-[#991B1B]"
            role="alert"
          >
            {errorMessage}
          </p>
        ) : null}

        {isBulk ? (
          <BulkDeleteList conversations={conversations} />
        ) : singleDetails ? (
          <div className="overflow-hidden rounded-xl border border-[#E5E7EB] bg-[#F8FAFC]">
            <div className="border-b border-[#E5E7EB] bg-[#EEF2FB]/70 px-4 py-2.5">
              <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#4A6BB6]">
                Chat summary
              </p>
            </div>
            <dl className="divide-y divide-[#E5E7EB]">
              {SUMMARY_ROWS.map(({ key, label }) => (
                <div
                  key={key}
                  className="flex flex-col gap-0.5 px-4 py-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4"
                >
                  <dt className="text-xs font-semibold text-[#64748B]">{label}</dt>
                  <dd className="text-sm font-semibold text-[#0F172A] sm:max-w-[62%] sm:text-right">
                    {singleDetails[key] ?? "—"}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ) : null}

        <div className="flex gap-3 rounded-xl border border-[#FECACA] bg-[#FEF2F2] px-2 py-3">
          <HiOutlineExclamationCircle
            className="mt-0.5 h-5 w-5 shrink-0 text-[#DC2626]"
            aria-hidden
          />
          <div className="min-w-0 space-y-1 text-xs text-[#991B1B]">
            <p className="font-bold text-[#0F172A]">This action cannot be undone</p>
            <p className="leading-relaxed">
              {isBulk && count > 1
                ? "All selected chats and their message history will be permanently removed from your workspace."
                : "This chat and its full message history will be permanently removed from your workspace."}
            </p>
          </div>
        </div>
      </div>
    </ModalSkeleton>
  );
};

export default Vertex360CopilotDeleteConfirmModal;
